import React, { useMemo } from 'react';
import { BsArchiveFill, BsGearFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Icon, IconButton } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../../../../../../../components/IconButtonMenu';
import PINSAIIcon from '../../../../../../../../../../components/PINSAIIcon';
import { analyticsIds } from '../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../../../../../../constants/featureTypes';
import { selectCompanyFeatures } from '../../../../../../../../../../redux/modules/company';
import { hideModal, showModal } from '../../../../../../../../../../redux/modules/modal';
import {
  useActiveRecordDocuments,
  useRecordQuestionRequirements
} from '../../../../../../../../../../services/record-service';
import ArchivedDocumentsModal from '../../modals/ArchivedDocumentsModal';
import AiRescanModal from '../AiRescanModal';

const DocumentIconTray = ({ companyId, insuredId, recordId, jobId }) => {
  const dispatch = useDispatch();
  const { isLoading: documentsLoading } = useActiveRecordDocuments({ recordId });
  const { isLoading: questionRequirementsLoading, data: questionRequirements } = useRecordQuestionRequirements(recordId);

  const companyFeatures = useSelector(selectCompanyFeatures);

  const questionsFeatureEnabled = useMemo(
    () => companyFeatures.find(feature => feature.name === FeatureTypes.AiTierOne)?.isEnabled,
    [companyFeatures]
  );

  const menuOptions = useMemo(() => {
    if (questionRequirementsLoading) {
      return [];
    }

    return [
      {
        label: 'PINS AI Assistant - Rescan',
        icon: PINSAIIcon,
        iconSize: '20px',
        id: 'rescan',
        isDisabled: !questionRequirements?.length,
        tooltip: !questionRequirements?.length ? 'No questions set for record' : undefined,
        handler: ({ e, dispatch, insuredId, recordId, jobId }) => {
          dispatch(showModal(() => (
            <AiRescanModal
              insuredId={insuredId}
              jobId={jobId}
              recordId={recordId}
            />
          ), {
            size: '4xl',
            overlayClose: true,
            variant: 'secondary',
            onClose: () => dispatch(hideModal())
          }));
        }
      }
    ];
  }, [questionRequirements, questionRequirementsLoading]);

  return (
    <Box fontSize={18} m={0} zIndex={5}>
      <Flex>
        <IconButton
          aria-label="archived-documents-modal-icon"
          data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.openArchivedDocumentsModal}
          data-cy={CypressIds.documentArchivedModalButton}
          dusk="open-archived-documents-modal-button"
          icon={<Icon as={BsArchiveFill} boxSize="20px" />}
          variant="textWhite"
          onClick={() => dispatch(showModal(() => <ArchivedDocumentsModal insuredId={insuredId} jobId={jobId} recordId={recordId} />, {
            actionRequired: false,
            maxWidth: 1040,
            title: 'Archived Documents'
          }))}
        />
        {questionsFeatureEnabled && (
          <IconButtonMenu
            ariaLabel="Document Requirements Menu"
            cypressId={documentsLoading ? CypressIds.loadingDocumentGearIcon : CypressIds.loadedDocumentGearIcon}
            iconElement={<Icon as={BsGearFill} boxSize="20px" />}
            id={recordId}
            isLoading={documentsLoading || questionRequirementsLoading}
            menuListWidth="260px"
            menuOptions={menuOptions}
            metadata={{
              recordId,
              insuredId,
              jobId,
              companyId
            }}
            variant="textWhite"
          />
        )}
      </Flex>
    </Box>
  );
};

DocumentIconTray.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default DocumentIconTray;
